import { IDynamicLinkedServiceTypeMetadata, ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { format } from 'app/shared/utils/string.util';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { ConnectionAuthKind, CredentialTypeOptions, dynamicsAxAuthTypes } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';
import { parameterizeDynamicsControlMetadata } from '../consts/metadata-consts';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { compact } from 'lodash';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';

const servicePrincipalControl: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'servicePrincipalId',
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        label: stringResources.ModelName_ServicePrincipalID,
        isLabelVertical: true
    },
    {
        type: 'dynamic-akv-switcher',
        bind: 'servicePrincipalKey',
        label: stringResources.ModelName_ServicePrincipalKey,
        child: {
            type: 'password',
            label: stringResources.ModelName_ServicePrincipalKey,
            required: true,
            isLabelVertical: true
        },
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template, stringResources.DynamicsAX_DisplayText,
            stringResources.ModelName_ServicePrincipalKey)
    }
];

const authenticationTypeControl: ControlMetadata = {
    type: 'select-switch',
    bind: 'authenticationType',
    label: stringResources.ModelName_AuthenticationType,
    options: [
        {
            label: stringResources.AuthenticationType_ServicePrincipal,
            value: dynamicsAxAuthTypes.ServicePrincipal
        },
        {
            label: stringResources.ModelName_UserAuthentication,
            value: dynamicsAxAuthTypes.UserAuth
        }
    ],
    required: true,
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    isLabelVertical: true,
    defaultValue: dynamicsAxAuthTypes.ServicePrincipal,
    cases: [
        {
            caseValue: dynamicsAxAuthTypes.ServicePrincipal,
            children: servicePrincipalControl.map(parameterizeDynamicsControlMetadata)
        },
        {
            caseValue: dynamicsAxAuthTypes.UserAuth,
            children: [
                {
                    type: 'credential-select',
                    bind: 'credential',
                    filterCredentialType: CredentialTypeOptions.UserAuth,
                    required: true,
                    validators: [
                        { type: 'required', errorMessage: '' }
                    ]
                }
            ]
        }
    ]
};

const urlControl: ControlMetadata = {
    type: 'textbox',
    bind: 'url',
    required: true,
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    placeholder: 'https://<organization-name>.ax.dynamics.com/data',
    label: stringResources.ModelName_Url,
    isLabelVertical: true
};

const aadResourceIdControl: ControlMetadata = {
    type: 'textbox',
    bind: 'aadResourceId',
    required: true,
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    placeholder: 'https://<organization-name>.ax.dynamics.com',
    label: stringResources.ModelName_AADResourceId,
    isLabelVertical: true
};

const tenantControl: ControlMetadata = {
    type: 'tenant-textbox',
    bind: 'tenant',
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    isLabelVertical: true,
    label: stringResources.ModelName_Tenant,
    required: true
};

const commonControls: ControlMetadata[] = [
    {
        type: 'constrained-control-pair',
        primary: parameterizeDynamicsControlMetadata(urlControl),
        secondary:
            parameterizeDynamicsControlMetadata(aadResourceIdControl),
        constrainedFunction: (val: string) => {
            try {
                const url = new URL(val);
                return url.origin ? url.origin : '';
            } catch (e) {
                return '';
            }
        }
    },
    parameterizeDynamicsControlMetadata(tenantControl)
];

export const dynamicsAXMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.dynamicsAX.linkedServiceType,
        supportedInTrident: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'DynamicsAX' as ResourceKind
        },
        supportedAuthKindsInTrident: compact([
            ConnectionAuthKind.ServicePrincipal,
            ConnectionAuthKind.WorkspaceIdentity
        ]),
        displayName: stringResources.DynamicsAX_DisplayText,
        supportOPDG: GlobalVariables.enableOPDG,
        supportCopyJobSource: true,
        supportedJobModesAsSource: GlobalVariables.copyJobIncrementalCopyPhase2 ? undefined : ['Batch'],
        linkedServiceKind: 'table',
        icon: {
            className: 'svg-i-100x100-dynamics_AX'
        },
        tableNameAlias: 'path',
        isFirstClass: true,
        supportDatasetV2: true,
        supportFirstClassParameterExperience: true,
        copySinkTypes: [],
        copySourceTypes: [adfModelTypeMap.dynamicsAX.copySourceType],
        category: ['ServicesApps'],
        onetimeQueryTemplate: '',
        // eslint-disable-next-line max-len
        timedQueryTemplate: '$filter=<<columnName>> ge datetime\'@{formatDateTime(pipeline().parameters.windowStart, \'yyyy-MM-ddTHH:mm:ssZ\' )}\' \nand <<columnName>> lt datetime\'@{formatDateTime(pipeline().parameters.windowEnd, \'yyyy-MM-ddTHH:mm:ssZ\' )}\'',
        deltaLoadQueryTemplate: `$filter=<<columnName>> ge @{if(contains(json(item().DataLoadingBehaviorSettings).watermarkColumnType, 'Int'),
        json(item().DataLoadingBehaviorSettings).watermarkColumnStartValue,
        concat('datetime''', formatDateTime(json(item().DataLoadingBehaviorSettings).watermarkColumnStartValue, 'yyyy-MM-ddTHH:mm:ssZ'), ''''))}
        and <<columnName>> lt @{if(contains(json(item().DataLoadingBehaviorSettings).watermarkColumnType, 'Int'),
        activity('GetMaxWatermarkValue').output.firstRow.CurrentMaxWaterMarkColumnValue,
        concat('datetime''', formatDateTime(activity('GetMaxWatermarkValue').output.firstRow.CurrentMaxWaterMarkColumnValue, 'yyyy-MM-ddTHH:mm:ssZ'), ''''))}`,
        controls: GlobalVariables.enableConnectorUserAuthCred
            ? commonControls.concat([authenticationTypeControl])
            : commonControls.concat(servicePrincipalControl.map(parameterizeDynamicsControlMetadata))
    };
    return metadata;
};
